/* eslint-disable complexity */
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Badge} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/badge.types';
import {Notification as NotificationType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/notifications.types';
import {COLORS, GenericIconName, PriceBox} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import {BadgeList} from '../../../features/BadgeList/BadgeList';
import {useBadgesList} from '../../../features/BadgeList/useBadgesList';
import {useIsCoinsActive} from '../../../features/Coins/utils/useIsCoinsActive';
import {useSelectedDiscountColor} from '../../Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {GUEST_USER_SUBSCRIBED_TO_NEWSLETTER} from '../components/NewsletterSignUp';
import {useNewsLetterSignUpStateManager} from '../useNewsLetterSignUpStateManager';
import {TrackedNotificationCenterCard} from './TrackedNotificationCenterCard';

export const NotificationCardList: FC<{
  notifications: NotificationType[];
}> = ({notifications}) => {
  const selectedDiscountColor = useSelectedDiscountColor();
  const {open: openNewsLetterSignUp} = useNewsLetterSignUpStateManager();
  const {isActive: isPriceDisplayOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  const isCoinsActive = useIsCoinsActive();

  return (
    <>
      {notifications.map((notification, index) => {
        switch (notification.item?.$case) {
          case 'almostSoldOut': {
            const almostSoldOut = notification.item.almostSoldOut;
            const tracker = {
              ...almostSoldOut.tracker,
              notificationType: notification.item?.$case,
            };
            const isHeroNotification = 'heroInfo' in almostSoldOut;

            return (
              <TrackedNotificationCenterCard
                {...almostSoldOut}
                tracker={tracker}
                key={`almostSoldOut-${index}`}
                headerIconName={
                  isHeroNotification
                    ? GenericIconName.Bell
                    : GenericIconName.Warning
                }
                forceWhiteImageBackground
                headline={almostSoldOut.headline}
                headerBackground={
                  isHeroNotification ? almostSoldOut.heroInfo?.color : undefined
                }
                info={
                  isHeroNotification
                    ? almostSoldOut.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
              />
            );
          }

          case 'tadaridaAlmostSoldOut': {
            const tadaridaAlmostSoldOut =
              notification.item.tadaridaAlmostSoldOut;
            const tracker = {
              ...tadaridaAlmostSoldOut.tracker,
              notificationType: notification.item?.$case,
            };

            return (
              <TrackedNotificationCenterCard
                {...tadaridaAlmostSoldOut}
                tracker={tracker}
                key={`tadaridaAlmostSoldOut-${index}`}
                headerIconName={GenericIconName.Warning}
                forceWhiteImageBackground
              />
            );
          }

          case 'generic': {
            const generic = notification.item.generic;
            const tracker = {
              ...generic.tracker,
              notificationType: notification.item?.$case,
            };
            const isHeroNotification = 'heroInfo' in generic;

            return (
              <TrackedNotificationCenterCard
                {...generic}
                headline={generic.headline}
                tracker={tracker}
                key={`almostSoldOut-${index}`}
                headerIconName={
                  isHeroNotification
                    ? GenericIconName.Bell
                    : GenericIconName.Warning
                }
                forceWhiteImageBackground
                headerBackground={
                  isHeroNotification ? generic.heroInfo?.color : undefined
                }
                info={
                  isHeroNotification
                    ? generic.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
              />
            );
          }

          case 'tadaridaGeneric': {
            const tadaridaGeneric = notification.item.tadaridaGeneric;
            const tracker = {
              ...tadaridaGeneric.tracker,
              notificationType: notification.item?.$case,
            };

            return (
              <TrackedNotificationCenterCard
                {...tadaridaGeneric}
                tracker={tracker}
                key={`tadaridaGeneric-${index}`}
                headerIconName={GenericIconName.Warning}
                forceWhiteImageBackground
              />
            );
          }

          case 'desiredProduct': {
            const desiredProduct = notification.item.desiredProduct;
            const tracker = {
              ...desiredProduct.tracker,
              notificationType: notification.item?.$case,
            };
            const isHeroNotification = 'heroInfo' in desiredProduct;

            return (
              <TrackedNotificationCenterCard
                {...desiredProduct}
                tracker={tracker}
                key={`almostSoldOut-${index}`}
                headerIconName={
                  isHeroNotification
                    ? GenericIconName.Bell
                    : GenericIconName.StarOutline
                }
                forceWhiteImageBackground
                headline={desiredProduct.headline}
                headerBackground={
                  isHeroNotification
                    ? desiredProduct.heroInfo?.color
                    : undefined
                }
                info={
                  isHeroNotification
                    ? desiredProduct.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
              />
            );
          }

          case 'priceDrop': {
            const priceDrop = notification.item.priceDrop;
            const tracker = {
              ...priceDrop.tracker,
              notificationType: notification.item?.$case,
            };
            const isHeroNotification = 'heroInfo' in priceDrop;

            return (
              <TrackedNotificationCenterCard
                {...priceDrop}
                tracker={tracker}
                headline={priceDrop.headline}
                key={`almostSoldOut-${index}`}
                headerIconName={
                  isHeroNotification
                    ? GenericIconName.Bell
                    : GenericIconName.Sale
                }
                headerIconColor={selectedDiscountColor ?? COLORS.RedSale}
                badgeList={
                  <BadgesList
                    badges={priceDrop.badges}
                    sale="SALE"
                    campaignSale="SALE"
                  />
                }
                priceBox={
                  <PriceBox
                    price={
                      isPriceDisplayOverhaulV2Active
                        ? priceDrop.priceV2
                        : priceDrop.price
                    }
                    hideBadges // the BE model supports badges in the reductions array, but the FE should not display them
                    hideVat
                    showSmallerFont
                    showReductionsInline
                    productId={priceDrop.id}
                  />
                }
                forceWhiteImageBackground
                headerBackground={
                  isHeroNotification ? priceDrop.heroInfo?.color : undefined
                }
                info={
                  isHeroNotification
                    ? priceDrop.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
              />
            );
          }

          case 'tadaridaPriceDrop': {
            const tadaridaPriceDrop = notification.item.tadaridaPriceDrop;
            const tracker = {
              ...tadaridaPriceDrop.tracker,
              notificationType: notification.item?.$case,
            };

            return (
              <TrackedNotificationCenterCard
                {...tadaridaPriceDrop}
                tracker={tracker}
                key={`tadaridaPriceDrop-${index}`}
                headerIconName={GenericIconName.Sale}
                headerIconColor={selectedDiscountColor ?? COLORS.RedSale}
                badgeList={
                  <BadgesList
                    badges={tadaridaPriceDrop.badges}
                    sale="SALE"
                    campaignSale="SALE"
                  />
                }
                forceWhiteImageBackground
              />
            );
          }

          case 'orderUpdate': {
            const orderUpdate = notification.item.orderUpdate;
            const tracker = {
              ...orderUpdate.tracker,
              notificationType: notification.item?.$case,
            };
            const isHeroNotification = 'heroInfo' in orderUpdate;

            return (
              <TrackedNotificationCenterCard
                {...orderUpdate}
                tracker={tracker}
                headline={orderUpdate.headline}
                key={`orderUpdate-${index}`}
                headerIconName={
                  isHeroNotification
                    ? GenericIconName.Bell
                    : GenericIconName.UspFreeShipping
                }
                headerBackground={
                  isHeroNotification ? orderUpdate.heroInfo?.color : undefined
                }
                info={
                  isHeroNotification
                    ? orderUpdate.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
              />
            );
          }

          case 'returnUpdate': {
            const returnUpdate = notification.item.returnUpdate;
            const tracker = {
              ...returnUpdate.tracker,
              notificationType: notification.item?.$case,
            };

            const isHeroNotification = 'heroInfo' in returnUpdate;

            return (
              <TrackedNotificationCenterCard
                {...returnUpdate}
                tracker={tracker}
                headline={returnUpdate.headline}
                key={`returnUpdate-${index}`}
                headerIconName={
                  isHeroNotification
                    ? GenericIconName.Bell
                    : GenericIconName.USPReturn
                }
                headerBackground={
                  isHeroNotification ? returnUpdate.heroInfo?.color : undefined
                }
                info={
                  isHeroNotification
                    ? returnUpdate.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
              />
            );
          }

          case 'sponsored': {
            const sponsored = notification.item.sponsored;
            const tracker = {
              ...sponsored.tracker,
              notificationType: notification.item?.$case,
            };
            const isHeroNotification = 'heroInfo' in sponsored;

            return (
              <TrackedNotificationCenterCard
                {...sponsored}
                tracker={tracker}
                headline={sponsored.headline}
                key={`returnUpdate-${index}`}
                headerIconName={
                  isHeroNotification
                    ? GenericIconName.Bell
                    : GenericIconName.Loudspeaker
                }
                badgeList={
                  <BadgesList
                    badges={sponsored.badges}
                    sale={null}
                    campaignSale={null}
                  />
                }
                forceWhiteImageBackground
                headerBackground={
                  isHeroNotification ? sponsored.heroInfo?.color : undefined
                }
                info={
                  isHeroNotification
                    ? sponsored.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
              />
            );
          }

          case 'saleEvent': {
            const saleEvent = notification.item.saleEvent;
            const tracker = {
              ...saleEvent.tracker,
              notificationType: notification.item?.$case,
            };
            const isHeroNotification = 'heroInfo' in saleEvent;

            return (
              <TrackedNotificationCenterCard
                {...saleEvent}
                tracker={tracker}
                headline={saleEvent.headline}
                key={`returnUpdate-${index}`}
                headerIconName={GenericIconName.SmallSaleEvent}
                forceWhiteImageBackground
                headerBackground={
                  isHeroNotification
                    ? saleEvent.heroInfo?.color || COLORS.RedSale
                    : COLORS.RedSale
                }
                info={
                  isHeroNotification
                    ? saleEvent.heroInfo?.infoConditions
                    : undefined
                }
                infoButtonTopAlignment
                fallbackIconReplacement={GenericIconName.SaleEvent}
              />
            );
          }

          case 'newsletterSignup': {
            if (GUEST_USER_SUBSCRIBED_TO_NEWSLETTER) {
              return null;
            }

            const newsletterSignup = notification.item.newsletterSignup;
            const tracker = {
              ...newsletterSignup.tracker,
              notificationType: notification.item?.$case,
            };

            return (
              <TrackedNotificationCenterCard
                {...newsletterSignup}
                tracker={tracker}
                key={`newsletterSignup-${index}`}
                headerIconName={GenericIconName.Mail}
                infoButtonTopAlignment
                onCardClick={openNewsLetterSignUp}
                preventRedirectOnClick
              />
            );
          }

          case 'coins': {
            if (!isCoinsActive) {
              return null;
            }

            return (
              <TrackedNotificationCenterCard
                key={`coins-${index}`}
                {...notification.item.coins}
                tracker={{
                  ...notification.item.coins.tracker,
                  notificationType: notification.item?.$case,
                }}
                headerIconName={GenericIconName.CoinsDoubleColor}
                forceWhiteImageBackground
              />
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};

const BadgesList: React.FC<{
  badges: Badge[];
  sale: string | null;
  campaignSale: string | null;
}> = ({badges, sale, campaignSale}) => {
  const badgesList = useBadgesList({
    badges,
    sale,
    campaignSale,
    showPriceOverhaulV2SaleLabel: false,
  });

  return <BadgeList badgesList={badgesList} />;
};
